import {
  type HighlightsController,
  type HighlightsRenderer,
} from '@databases/highlights';
import { HIGHLIGHT_FILL_PREVIEW_CLASS } from '@databases/highlights/constants';

import { createEffect } from '../utils/create-effect';

import { FILL_PREVIEW_LAYER_Z_INDEX } from './constants';

/**
 * Creates the effect that draws the dashed preview box while the user
 * is dragging the fill handle.
 *
 * Listens to:
 * - `fillPreview`  — draws the dashed box at the current drag extent
 * - `fillApplied`  — clears the dashed box when the fill is committed
 * - `fillStarted`  — clears the dashed box when a new fill drag begins
 */
export const createFillPreviewEffect = createEffect(
  (controller: HighlightsController) => {
    // ---------------------------------------------------------------------------
    // DOM
    // ---------------------------------------------------------------------------
    let scrollableContainer: HTMLElement | null = null;

    // ---------------------------------------------------------------------------
    // Renderers
    // ---------------------------------------------------------------------------

    // Dedicated layer for the dashed preview box
    let fillPreviewLayer: HighlightsRenderer | null = null;

    function ensureDependentElementsAndRenderer() {
      controller.mount();

      if (!scrollableContainer) {
        scrollableContainer = controller.getScrollContainer();
      }

      // Setup the preview layer
      if (!fillPreviewLayer && scrollableContainer) {
        fillPreviewLayer = controller.createLayer(FILL_PREVIEW_LAYER_Z_INDEX);
      }

      return !!(fillPreviewLayer && scrollableContainer);
    }

    return {
      deltaTypes: ['fillStarted', 'fillPreview', 'fillApplied'],

      dispose() {
        // Cleanup the preview layer
        if (fillPreviewLayer) {
          controller.removeLayer(fillPreviewLayer);

          fillPreviewLayer = null;
        }

        scrollableContainer = null;
      },

      onDelta(delta) {
        if (
          !ensureDependentElementsAndRenderer() ||
          !fillPreviewLayer ||
          !scrollableContainer
        ) {
          return;
        }

        // Handle the dashed preview box logic
        if (delta.kind === 'fillPreview') {
          fillPreviewLayer.clear();

          fillPreviewLayer.add(
            {
              id: 'fill-preview-dashed-box',
              anchor: {
                row: delta.fillRange.anchor.row.index,
                col: delta.fillRange.anchor.col.index,
              },
              lead: {
                row: delta.fillRange.lead.row.index,
                col: delta.fillRange.lead.col.index,
              },
            },
            'cell',
            HIGHLIGHT_FILL_PREVIEW_CLASS
          );

          return;
        }

        if (delta.kind === 'fillApplied' || delta.kind === 'fillStarted') {
          fillPreviewLayer.clear();
        }
      },
    };
  }
);
